import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';

import { CustomFormArray } from './custom-form-array';
import { CustomFormControl } from './custom-form-control';
import {
  ConditionalControlOption,
  ConditionalControlSet,
  CustomFormGroup,
  UpdateOptions,
} from './custom-form-group';

/**
 * Form Helpers - Utility functions for working with custom form components
 *
 * @description
 * This module provides a collection of utility functions designed to work with
 * custom form components. Key functionality includes:
 * - Type guards for checking control types
 * - Form traversal and recursive processing
 * - Value transformation and manipulation
 * - Control synchronization between form structures and data
 */

/**
 * Type guards for form control types - improves code readability and type checking
 *
 * @description
 * These type guard functions provide type safety when working with different
 * control types in TypeScript. They help the compiler understand what methods
 * and properties are available on a control after the type check.
 */

/**
 * Checks if a control is a CustomFormControl instance
 * @param control The control to check
 * @returns True if the control is a CustomFormControl
 */
export function isCustomFormControl(
  control: AbstractControl
): control is CustomFormControl {
  return control instanceof CustomFormControl;
}

/**
 * Checks if a control is a CustomFormGroup instance
 * @param control The control to check
 * @returns True if the control is a CustomFormGroup
 */
export function isCustomFormGroup(
  control: AbstractControl
): control is CustomFormGroup {
  return control instanceof CustomFormGroup;
}

/**
 * Checks if a control is a CustomFormArray instance
 * @param control The control to check
 * @returns True if the control is a CustomFormArray
 */
export function isCustomFormArray(
  control: AbstractControl
): control is CustomFormArray {
  return control instanceof CustomFormArray;
}

/**
 * Checks if a control is any FormGroup instance (including CustomFormGroup)
 * @param control The control to check
 * @returns True if the control is a FormGroup or CustomFormGroup
 */
export function isFormGroup(control: AbstractControl): control is FormGroup {
  return control instanceof FormGroup;
}

/**
 * Checks if a control is any FormArray instance (including CustomFormArray)
 * @param control The control to check
 * @returns True if the control is a FormArray or CustomFormArray
 */
export function isFormArray(control: AbstractControl): control is FormArray {
  return control instanceof FormArray;
}

/**
 * Checks if a control is a FormControl instance
 * @param control The control to check
 * @returns True if the control is a FormControl
 */
export function isFormControl(
  control: AbstractControl
): control is FormControl {
  return control instanceof FormControl;
}

/**
 * Helper function for setting values to form controls with appropriate method (setValue or patchValue)
 *
 * @description
 * This utility function intelligently sets values on any AbstractControl by:
 * - Using setValue for FormControls (with special handling for CustomFormControls)
 * - Using patchValue for FormGroups and FormArrays
 *
 * @param control The control to set the value on
 * @param value The value to set
 * @param options Options for controlling the update behavior
 *
 * @example
 * ```typescript
 * // With a regular FormControl
 * const nameControl = new FormControl('');
 * setValueToControl(nameControl, 'John'); // Sets value to 'John'
 *
 *
 * // Will set the value because it meets the condition
 * setValueToControl(emailControl, 'user@example.com');
 *
 * // Won't change the value because it doesn't meet the condition
 * setValueToControl(emailControl, 'invalid-email');
 *
 * // With a FormGroup or FormArray, uses patchValue
 * const userForm = new FormGroup({
 *   name: new FormControl(''),
 *   email: new FormControl('')
 * });
 * setValueToControl(userForm, { name: 'John' }); // Patches only the name
 * ```
 */
export function setValueToControl(
  control: AbstractControl,
  value: any,
  options?: UpdateOptions
): void {
  if (!control || value === undefined) return;

  if (control instanceof FormControl) {
    control.setValue(value, options);
  } else if (control instanceof FormGroup || control instanceof FormArray) {
    control.patchValue(value, options);
  }
}

/**
 * Helper function for finding a matching conditional control option
 * Optimized for performance in large forms
 * @param controlSet The set of conditional controls to search
 * @param value The value object to check against
 * @returns The matching option, key, and value if found
 */
export function findMatchingConditionalControl(
  controlSet: ConditionalControlSet,
  value: { [key: string]: any }
): { option?: ConditionalControlOption; key?: string; foundValue?: any } {
  if (!controlSet?.conditions?.length || !value) {
    return { option: undefined, key: undefined, foundValue: undefined };
  }

  const valueKeys = Object.keys(value);
  if (valueKeys.length === 0) {
    return { option: undefined, key: undefined, foundValue: undefined };
  }

  const valueKeysSet = new Set(valueKeys);
  const conditions = controlSet.conditions;
  const conditionsLength = conditions.length;

  for (let i = 0; i < conditionsLength; i++) {
    const condition = conditions[i];
    const key = condition.key;

    if (valueKeysSet.has(key)) {
      return {
        option: condition,
        key,
        foundValue: value[key],
      };
    }
  }

  return { option: undefined, key: undefined, foundValue: undefined };
}

/**
 * Recursively processes any AbstractControl to handle CustomFormControl,
 * CustomFormGroup, CustomFormArray, and other control types appropriately
 *
 * @description
 * This is a powerful utility that recursively handles form patching for deeply nested structures.
 * It supports all the custom form components and handles edge cases like:
 * - Circular references in form structures
 * - Multi-key lookups in deeper nested levels
 * - Array control synchronization
 * - Proper event propagation control
 *
 * This is primarily an internal utility used by CustomFormGroup's patchValue implementation.
 *
 * @param control The AbstractControl to process
 * @param value The value object to check
 * @param options The options to use when setting values
 * @param processedControls Optional Map to track already processed controls and prevent circular references
 *
 * @example
 * ```typescript
 * // For a complex nested form
 * const orderForm = new FormGroup({
 *   items: new FormArray([]),
 *   customer: new FormGroup({
 *     address: new CustomFormGroup({...})
 *   })
 * });
 *
 * // Process the entire structure with a complex data object
 * processNestedControls(orderForm, complexOrderData);
 * ```
 */
export function processNestedControls(
  control: AbstractControl,
  value: any = null,
  options?: UpdateOptions,
  processedControls: Map<AbstractControl, boolean> = new Map()
): void {
  if (!control) return;

  if (processedControls.has(control)) return;
  processedControls.set(control, true);

  // Handle FormControl
  if (isFormControl(control)) {
    processBasicFormControl(control, value, options);

    // Handle FormGroup
  } else if (isFormGroup(control)) {
    // Process conditional controls for CustomFormGroup
    processConditionalControls(control, value);

    //Process FormGroup nested controls
    processNestedFormGroup(control, value, options, processedControls);

    // Handle FormArray
  } else if (isFormArray(control)) {
    processNestedFormArrays(control, value, options, processedControls);
  }
}

/**
 * Processes a nested control within a form group based on its type
 *
 * @description
 * This function examines the type of a child control and delegates to the
 * appropriate processing function. It handles:
 * - FormControl with special handling for CustomFormControl features
 * - FormGroup with recursive processing of its children
 * - FormArray with array-specific synchronization logic
 *
 * @param childControl The child control to process
 * @param childValue The direct value to apply to this child control
 * @param value The parent value object (used for multi-key lookups)
 * @param batchOptions Options for controlling the update behavior
 * @param processedControls Map to track already processed controls
 */
function processNestedControlsInFormGroup(
  childControl: AbstractControl,
  childValue: any,
  value: any,
  batchOptions?: UpdateOptions,
  processedControls: Map<AbstractControl, boolean> = new Map()
) {
  // Handle FormControl
  if (isFormControl(childControl)) {
    processBasicFormControl(childControl, childValue, batchOptions, value);
  } else if (isFormGroup(childControl)) {
    processNestedControls(
      childControl,
      childValue,
      batchOptions,
      processedControls
    );
  } else if (isFormArray(childControl)) {
    processNestedFormArrays(
      childControl,
      childValue,
      batchOptions,
      processedControls
    );
  }
}

/**
 * Processes all controls within a FormGroup including existing and dynamic controls
 *
 * @description
 * This function handles the complete processing of a FormGroup by:
 * - Processing all existing child controls with their corresponding values
 * - Creating dynamic controls for extra data not defined in the form structure
 * - Updating form validity after all controls are processed
 *
 * The function carefully manages event propagation to prevent unnecessary validation
 * events while controls are being updated, then triggers a single validation event
 * at the end if required.
 *
 * @param control The FormGroup to process
 * @param value The value object to patch into the form
 * @param options Options for controlling the update behavior
 * @param processedControls Map to track already processed controls
 */
function processNestedFormGroup(
  control: FormGroup,
  value: any,
  options?: UpdateOptions,
  processedControls: Map<AbstractControl, boolean> = new Map()
) {
  const controlsObj = control.controls;
  const isObject = typeof value === 'object' && value !== null;

  const batchOptions = {
    ...options,
    emitEvent: false,
    onlySelf: options?.onlySelf === true,
  };

  const controlKeys = Object.keys(controlsObj);
  const controlKeysLength = controlKeys.length;

  // Process existing controls
  for (let i = 0; i < controlKeysLength; i++) {
    const key = controlKeys[i];
    const childControl = controlsObj[key];
    if (!childControl) continue;

    const childValue = isObject ? value[key] : null;

    processNestedControlsInFormGroup(
      childControl,
      childValue,
      value,
      batchOptions,
      processedControls
    );
  }

  // Process dynamic controls once after all existing controls have been processed
  processDynamicControls(control, value);

  if (options?.emitEvent) {
    control.updateValueAndValidity({
      onlySelf: options?.onlySelf,
      emitEvent: true,
    });
  }
}

/**
 * Processes conditional controls in a CustomFormGroup
 *
 * @description
 * This function checks if the control is a CustomFormGroup and has conditional controls defined.
 * If so, it calls the processConditionalControls method to handle the creation, updating, or
 * removal of conditional controls based on the value object.
 *
 * Conditional controls are dynamically added or removed based on the presence of specific
 * keys in the value object, allowing forms to adapt their structure to the data.
 *
 * @param control The control to check for conditional controls
 * @param value The value object to check against conditional rules
 */
function processConditionalControls(control: AbstractControl, value: any) {
  if (isCustomFormGroup(control)) {
    const conditionalControls = control.conditionalControls;
    if (conditionalControls && conditionalControls.length > 0) {
      control.processConditionalControls(value);
    }
  }
}

/**
 * Processes a FormArray and its child controls with array data
 *
 * @description
 * This function handles the special requirements of FormArray processing:
 * - For CustomFormArray, it synchronizes the number of controls to match the data array length
 * - Processes each control in the array with its corresponding data item
 * - Handles cases where the data array is shorter than the control array
 *
 * This approach ensures that the FormArray's structure matches the data being patched,
 * automatically adding or removing controls as needed for CustomFormArray instances.
 *
 * @param control The FormArray to process
 * @param value The array data to process (defaults to empty array)
 * @param options Options for controlling the update behavior
 * @param processedControls Map to track already processed controls
 */
function processNestedFormArrays(
  control: FormArray,
  value: any = [],
  options?: UpdateOptions,
  processedControls: Map<AbstractControl, boolean> = new Map()
) {
  !value && (value = []);
  if (Array.isArray(value)) {
    if (isCustomFormArray(control)) {
      control.syncControls(value);
    }

    const controlArray = control.controls;
    const valueLength = value.length;

    for (let i = 0; i < controlArray.length; i++) {
      const arrayControl = controlArray[i];
      const arrayItemValue = i < valueLength ? value[i] : null;
      processNestedControls(
        arrayControl,
        arrayItemValue,
        options,
        processedControls
      );
    }
  }
}

/**
 * Processes a CustomFormControl by checking its multiKeyOptions against a value object
 * @param control The CustomFormControl to process
 * @param value The value object to check
 * @param options The options to use when setting the value
 */
export function processCustomFormControlKeys(
  control: CustomFormControl,
  value: any,
  options?: UpdateOptions
): void {
  if (!control) return;

  const foundValue = control.findValueFromSourceKeys(value);

  if (foundValue !== undefined) {
    control.setValue(foundValue, options);
  } else if (control.multiKeyOptions && control.value) {
    // Use smart reset to determine appropriate default value
    const resetValue = getSmartResetValue(control.value);

    control.reset(resetValue, {
      ...options,
      onlySelf: true,
    });
  }
}

/**
 * Processes a FormControl with special handling for CustomFormControl features
 *
 * @description
 * This function handles the specialized processing needed for FormControl instances,
 * with particular focus on the enhanced features of CustomFormControl:
 * - Multi-key options: Searches for values from multiple possible keys in the parent object
 * - Reset when unpatched: Resets controls to appropriate default values when no data is provided
 *
 * This allows CustomFormControl to handle complex data mapping scenarios where field
 * names in the data might not match control names in the form.
 *
 * @param control The FormControl to process
 * @param value The direct value for this control
 * @param options Options for controlling the update behavior
 * @param parentValue The parent value object (used for multi-key lookups)
 */
function processBasicFormControl(
  control: FormControl,
  value: any,
  options?: UpdateOptions,
  parentValue?: any
) {
  if (isCustomFormControl(control)) {
    if (control.multiKeyOptions) {
      processCustomFormControlKeys(control, parentValue, options);
    } else if (control.resetWhenUnpatched) {
      resetUnpatchedControls(control, value, options);
    }
  }
}

/**
 * Resets controls when no data is provided for patching
 *
 * @description
 * This function implements the "reset when unpatched" behavior for controls.
 * When a control has a value but no new value is provided (null/undefined),
 * it resets the control to an appropriate default value based on its current type.
 *
 * This prevents stale data from persisting in the form when it's no longer
 * present in the source data, making forms more accurately reflect the
 * current state of the data.
 *
 * @param control The control to potentially reset
 * @param value The value to check (will reset if null/undefined)
 * @param options Options for controlling the reset behavior
 */
function resetUnpatchedControls(
  control: AbstractControl,
  value: any,
  options?: UpdateOptions
): void {
  if (!control) return;

  if (control.value && (value === null || value === undefined)) {
    // Smart reset: use appropriate default value based on control's original value type
    const resetValue = getSmartResetValue(control.value);

    control.reset(resetValue, { onlySelf: true, ...options });
  }
}

/**
 * Determines the appropriate reset value based on the current value's type
 * @param currentValue The current value of the control
 * @returns The appropriate reset value: [] for arrays, false for booleans, null for everything else
 */
function getSmartResetValue(currentValue: any): any {
  // If the current value is an array, reset to empty array
  if (Array.isArray(currentValue)) {
    return [];
  }
  // If the current value is a boolean, reset to false
  else if (typeof currentValue === 'boolean') {
    return false;
  }
  // Everything else gets reset to null
  return null;
}

/**
 * Checks if data is meaningful enough to warrant creating a dynamic control
 * Ignores empty strings, 0, empty arrays, null, undefined, etc.
 *
 * @param value The value to check
 * @returns True if the data is meaningful, false otherwise
 */
function isDataMeaningful(value: any): boolean {
  // Handle null or undefined
  if (value === null || value === undefined) {
    return false;
  }

  // Handle different types
  switch (typeof value) {
    case 'string':
      return value.trim() !== ''; // Non-empty strings only

    case 'number':
      return value !== 0; // Non-zero numbers only

    case 'boolean':
      return true; // Booleans are always meaningful

    case 'object':
      if (Array.isArray(value)) {
        return value.length > 0; // Non-empty arrays only
      } else {
        return Object.keys(value).length > 0; // Non-empty objects only
      }

    default:
      return false; // Ignore other types
  }
}

/**
 * Creates or updates dynamic controls for data that doesn't have corresponding form controls.
 * Only creates controls for meaningful data (non-empty strings, non-zero numbers, non-empty arrays/objects).
 * Removes dynamic controls when their corresponding data is no longer present.
 * Prevents creation of dynamic controls that could conflict with conditional controls.
 *
 * @param control The form group to process
 * @param value The value object to check for extra data
 * @param options Standard Angular form options
 */
function processDynamicControls(
  control: FormGroup,
  value: { [key: string]: any }
): void {
  if (!value || typeof value !== 'object') {
    value = {};
  }

  // First, check for existing dynamic controls that need to be removed
  // if their data is no longer present in the value object
  cleanupDynamicControls(control, value);

  // Get set of conditional control keys to prevent creating dynamic controls with the same names
  const conditionalControlKeys = new Set<string>();
  if (isCustomFormGroup(control) && control.conditionalControls?.length) {
    for (const conditionalSet of control.conditionalControls) {
      if (conditionalSet.conditions?.length) {
        for (const condition of conditionalSet.conditions) {
          conditionalControlKeys.add(condition.key);
        }
      }
    }
  }

  // Then create dynamic controls for extra data
  const valueKeys = Object.keys(value);
  const controlKeys = Object.keys(control.controls);

  for (const key of valueKeys) {
    // Skip if this key already has a corresponding control or matches a conditional control key
    if (controlKeys.includes(key) || conditionalControlKeys.has(key)) {
      continue;
    }

    const dataValue = value[key];

    // Only create dynamic controls for meaningful data
    if (isDataMeaningful(dataValue)) {
      // Create a dynamic control for this data
      const dynamicControl = new CustomFormControl(dataValue, {
        isDynamicallyCreated: true,
      });

      // Add the control to the form group
      control.addControl(key, dynamicControl);
    }
  }
}

/**
 * Removes dynamic controls whose data is no longer present in the value object
 *
 * @param control The form group to process
 * @param value The current value object
 */
function cleanupDynamicControls(
  control: FormGroup,
  value: { [key: string]: any }
): void {
  const valueKeys = Object.keys(value);
  const controlsToRemove: string[] = [];
  // Identify dynamic controls to remove

  for (const key of Object.keys(control.controls)) {
    const formControl = control.controls[key];

    // Only check controls that were dynamically created
    if (isCustomFormControl(formControl) && formControl.isDynamicallyCreated) {
      // If the key doesn't exist in the value object, or the data is no longer meaningful,
      // mark the control for removal
      if (!valueKeys.includes(key) || !isDataMeaningful(value[key])) {
        controlsToRemove.push(key);
      }
    }
  }

  // Remove the identified controls
  for (const key of controlsToRemove) {
    control.removeControl(key);
  }
}

/**
 * Custom ErrorStateMatcher that checks both the local control and a parent control for errors.
 * Use this when a child input needs to reflect the error state of a parent FormControl.
 *
 * @example
 * ```typescript
 * // In component:
 * errorMatcher = new ParentControlErrorStateMatcher(this.parentControl);
 *
 * // In template:
 * <input [errorStateMatcher]="errorMatcher" />
 * ```
 */
export class ParentControlErrorStateMatcher implements ErrorStateMatcher {
  constructor(private readonly parentControl: FormControl | undefined) {}

  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const isSubmitted = form?.submitted;
    const hasLocalError = !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
    const hasParentError = !!(
      this.parentControl?.errors &&
      (this.parentControl.dirty || this.parentControl.touched || isSubmitted)
    );
    return hasLocalError || hasParentError;
  }
}
